// Shared PDF "chrome" (header logo + footer) for all html2pdf.js exports.
//
// html2pdf.js renders content to a canvas and slices it across pages; it does
// NOT natively repeat an HTML header/footer per page. So we drive the worker up
// to the jsPDF stage and draw the header logo + footer text directly onto EVERY
// page via the jsPDF API, then save. Callers must reserve top/bottom margin
// space (see PDF_MARGIN) so content never overlaps the header/footer bands.
import logo from "@/assets/logo";

// A4 portrait, millimetres.
const PAGE_W = 210;
const PAGE_H = 297;

// Header/footer band sizing (mm).
const LOGO_W = 14;
const LOGO_H = 14;
const LOGO_TOP = 6;
const FOOTER_Y = PAGE_H - 8;
const RULE_COLOR = [165, 18, 47]; // crimson brand rule
const TEXT_COLOR = [100, 116, 139]; // slate-500

// Content margins that leave room for the stamped header (top) + footer (bottom).
// [top, left, bottom, right] in mm. Logo band ends at LOGO_TOP(6)+LOGO_H(14)=20mm,
// so ~23mm top clears it with a small gap and no excess whitespace.
export const PDF_MARGIN = [23, 10, 16, 10];

function _formatDate(d = new Date()) {
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

// Draw the logo header + SalesTap/date/page footer on every page of a jsPDF doc.
function _drawChrome(pdf) {
  const total = pdf.internal.getNumberOfPages();
  const dateStr = _formatDate();
  const centerX = PAGE_W / 2;

  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i);

    // --- Header: logo only, centered ---
    try {
      pdf.addImage(logo, "PNG", centerX - LOGO_W / 2, LOGO_TOP, LOGO_W, LOGO_H);
    } catch (e) {
      // If the image can't be drawn on a given page, skip it rather than fail.
    }

    // --- Footer: crimson rule + SalesTap | date | Page X of Y ---
    pdf.setDrawColor(RULE_COLOR[0], RULE_COLOR[1], RULE_COLOR[2]);
    pdf.setLineWidth(0.4);
    pdf.line(10, FOOTER_Y - 4, PAGE_W - 10, FOOTER_Y - 4);

    pdf.setFontSize(8);
    pdf.setTextColor(TEXT_COLOR[0], TEXT_COLOR[1], TEXT_COLOR[2]);
    pdf.text("SalesTap", 10, FOOTER_Y);
    pdf.text(dateStr, centerX, FOOTER_Y, { align: "center" });
    pdf.text(`Page ${i} of ${total}`, PAGE_W - 10, FOOTER_Y, { align: "right" });
  }
}

// Run an html2pdf worker to completion WITH the header/footer stamped on every
// page. `worker` is the result of html2pdf().set(opt).from(el).
export async function saveWithChrome(worker) {
  await worker
    .toPdf()
    .get("pdf")
    .then((pdf) => {
      _drawChrome(pdf);
    })
    .save();
}

// Header/footer markup for the browser Print path (iframe). Printed via CSS so
// it appears at the top/bottom of the printed page; @page margins reserve space.
export function printChromeStyles() {
  return (
    `@page{margin:20mm 12mm 16mm 12mm;}` +
    `body{margin:0;font-family:Arial,Helvetica,sans-serif;` +
    `-webkit-print-color-adjust:exact;print-color-adjust:exact;}` +
    `table{border-collapse:collapse;}` +
    `.stw-print-header{position:fixed;top:-15mm;left:0;right:0;text-align:center;}` +
    `.stw-print-header img{height:10mm;}` +
    `.stw-print-footer{position:fixed;bottom:-11mm;left:0;right:0;` +
    `border-top:1.5px solid #A5122F;padding-top:2mm;display:flex;` +
    `justify-content:space-between;font-size:9px;color:#64748b;}`
  );
}

// Build & prepend the print header/footer as DOM NODES (no HTML string / no
// insertAdjacentHTML) so there is zero HTML-injection surface. `logo` is a
// trusted base64 data URI; date text is set via textContent.
export function appendPrintChrome(doc) {
  const dateStr = _formatDate();

  const header = doc.createElement("div");
  header.className = "stw-print-header";
  const img = doc.createElement("img");
  img.src = logo;
  img.alt = "SalesTap";
  header.appendChild(img);

  const footer = doc.createElement("div");
  footer.className = "stw-print-footer";
  const left = doc.createElement("span");
  left.textContent = "SalesTap";
  const mid = doc.createElement("span");
  mid.textContent = dateStr;
  const right = doc.createElement("span");
  footer.appendChild(left);
  footer.appendChild(mid);
  footer.appendChild(right);

  doc.body.insertBefore(footer, doc.body.firstChild);
  doc.body.insertBefore(header, doc.body.firstChild);
}
